/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { QSButton, QSTypography } from '../../../../../../shared/components/atoms';
import { BORDER_COLOR, Country } from '../../../../../../shared/typings';
import { FormControl, TextField } from '@mui/material';
import { WabaIntegration } from '../../../../../../shared/typings/waba';
import { useAtom, useAtomValue } from 'jotai';
import { phoneNumberAtom } from '../../../../../login/v1/state';
import { getCountryById } from '../../../../../../shared/utils/countries-mapper';
import PhoneDropdown from '../../../../../../shared/components/molecules/country-picker';
import validateAndTruncatePhoneNumber, { validatePhoneNumber } from '../../../../../../shared/utils/validate-phone';
import { getCustomerDetails } from '../../../requests/get-customer-details';
import useConversationNavigate from '../../../../../../shared/hooks/use-conversation-navigate';
import WabaSelector from '../../../../../../shared/components/molecules/waba-selector';
import APIError from '../../../../../../shared/errors/api-error';
import useEmbedClient from '../../../../../embed/hooks/use-embed-client';
import { EmbedClient } from '../../../../../login/v1/typings';
import useNotifications from '../../../../../../shared/hooks/use-notifications';

interface NewChatMainProps {
  setNewChat: Dispatch<SetStateAction<boolean>>;
}

interface FormValues {
  phone: string;
  selectedCountryId: string;
  wabaIntegration: WabaIntegration | undefined;
}

interface ErrorType {
  code?: string;
  message?: string;
  requiredPermissions?: string[];
}
const NewChatMain = ({ setNewChat }: NewChatMainProps) => {
  const selectedPhoneDetails = useAtomValue(phoneNumberAtom);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const embedClient = useEmbedClient();
  const { addNotification } = useNotifications();
  const isClientSalesforce = embedClient === EmbedClient.SALESFORCE || embedClient === EmbedClient.SALESFORCEV2;

  const [formData, setFormData] = useState<FormValues>({
    phone: '',
    selectedCountryId: selectedPhoneDetails.country.id,
    wabaIntegration: undefined,
  });

  const [{ country }, setPhoneNumber] = useAtom(phoneNumberAtom);

  const [isValidPhone, setIsValidPhone] = useState(false);
  const setFormValue = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [event.target.id]: event.target.value });
  };

  const selectedCountry = getCountryById(formData.selectedCountryId);

  const setCountry = useCallback(
    (country: Country) => {
      setPhoneNumber((no) => ({
        ...no,
        country,
      }));
      setFormData((prev) => ({
        ...prev,
        selectedCountryId: country.id,
      }));
    },
    [setPhoneNumber]
  );

  const selectWaba = useCallback((wabaIntegration: WabaIntegration) => {
    setFormData((prev) => ({
      ...prev,
      wabaIntegration,
    }));
  }, []);

  const buttonDisabled = !formData.wabaIntegration || !isValidPhone;

  const { navigateToConversation } = useConversationNavigate();

  useEffect(() => {
    setIsValidPhone(
      validatePhoneNumber({
        phone: formData.phone,
        countryCode: selectedCountry.countryCode || 'IN',
        setCountry,
      })
    );
  }, [formData.phone, selectedCountry.countryCode, setCountry]);

  const handleOnClickStart = useCallback(async () => {
    if (!formData.wabaIntegration) return;
    const phone = validateAndTruncatePhoneNumber({
      phone: formData.phone,
      countryCode: selectedCountry.countryCode,
      countrySymbol: selectedCountry.symbol,
    }).replace('+', '');
    const integrationWabaNumber = formData.wabaIntegration?.phoneNumber ?? '';
    try {
      setLoading(true);
      setError(null);
      const customerDetails = await getCustomerDetails({
        customerId: phone,
        integrationWabaNumber,
        source: isClientSalesforce ? 'SALESFORCE' : undefined,
      });
      setNewChat(false);
      setFormData({
        ...formData,
        phone: '',
      });
      navigateToConversation({
        phoneNumber: phone,
        chatTypeId: customerDetails.customerId,
        waba: formData.wabaIntegration.phoneNumber,
      });
    } catch (error) {
      if (error instanceof APIError) {
        setError({
          message: error.message,
          code: error.code,
          requiredPermissions: error.requiredPermissions,
        });
        if (error.requiredPermissions?.length) {
          addNotification({
            message: error.message,
            type: 'error',
            autoHideDuration: 4000,
          });
        }
      } else {
        setError({ message: (error as Error).message ?? 'An unknown error occurred' });
      }
    } finally {
      setLoading(false);
    }
  }, [
    formData,
    navigateToConversation,
    selectedCountry.countryCode,
    selectedCountry.symbol,
    setNewChat,
    embedClient,
    isClientSalesforce,
    addNotification,
  ]);

  return (
    <>
      <div
        css={css`
          height: 100%;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        `}
      >
        <div
          css={css`
            width: 100%;
            padding: 12px;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          `}
        >
          <QSTypography
            css={css`
              font-size: 14px;
              font-weight: 400;
              line-height: normal;
              margin-bottom: 8px;
            `}
          >
            Enter customer's phone number
          </QSTypography>
          <div
            css={css`
              width: 100%;
              display: flex;
              flex-direction: row;
              align-items: flex-end;
              justify-content: center;
            `}
          >
            <FormControl
              css={css`
                display: inline-flex;
                flex: 0 0 auto;
              `}
              variant="standard"
            >
              <PhoneDropdown value={country} onChange={setCountry} />
            </FormControl>
            <TextField
              id="phone"
              autoFocus
              value={formData.phone}
              placeholder={'Enter phone number'}
              sx={{ flexGrow: 1, padding: '0 8px' }}
              type="tel"
              InputLabelProps={{
                shrink: true,
              }}
              variant="standard"
              onChange={setFormValue}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !buttonDisabled) {
                  event.preventDefault();
                  handleOnClickStart();
                }
              }}
            />
          </div>
          {error && !error.requiredPermissions?.length && (
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 600;
                color: #ef5350;
                padding-top: 4px;
              `}
            >
              {error.message ? `${error.message}. ` : ''}Please try again.
            </QSTypography>
          )}
        </div>
        <div
          css={css`
            width: 100%;
            flex: 1;
            border-bottom: 1px solid ${BORDER_COLOR};
          `}
        >
          <WabaSelector onChangeWaba={selectWaba} hideInactiveWabas={true} />
        </div>
        <div
          css={css`
            display: flex;
            padding: 12px 20px;
            align-items: flex-start;
            gap: 14px;
            align-self: stretch;
          `}
        >
          <QSButton
            css={css`
              display: flex;
              height: 40px;
              padding: 12px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              border-radius: 8px;
              border: 1px solid #e0e0e9;
              background: #fff;
              color: inherit;
              outline: none;
              box-shadow: none;

              &:hover {
                background: #f5f5f8;
                outline: none;
                box-shadow: none;
              }
            `}
            onClick={() => setNewChat(false)}
          >
            Cancel
          </QSButton>
          <QSButton
            css={css`
              display: flex;
              height: 40px;
              padding: 12px 16px;
              justify-content: center;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              border-radius: 8px;
              background: #538d6f;
              box-shadow: none;

              &:hover {
                background: #538d6f;
                outline: none;
                box-shadow: none;
              }
            `}
            disabled={buttonDisabled}
            onClick={handleOnClickStart}
          >
            Start Chat
          </QSButton>
        </div>
      </div>
    </>
  );
};

export default NewChatMain;
